/**
 * Central transaction date/time resolution for reports, cash flow, and exports.
 * Sales/receipts → resolveSaleReceiptDate; expenses → business date + payment time.
 */

import { resolveSaleReceiptDate } from './saleReceiptDateTime';
import { formatSafeDate, formatSafeTime, formatSafeDateTime } from './formatSafety';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseInstant(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const parsed = typeof value === 'number' ? new Date(value) : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function isDateOnlyValue(value) {
  if (!value) return false;
  const s = String(value).trim();
  if (DATE_ONLY_RE.test(s)) return true;
  const parsed = parseInstant(value);
  if (!parsed) return false;
  const raw = String(value);
  return (
    !raw.includes('T') &&
    !raw.includes(':') &&
    parsed.getHours() === 0 &&
    parsed.getMinutes() === 0 &&
    parsed.getSeconds() === 0
  );
}

function combineDateWithInstantTime(dateOnlyStr, timeSource) {
  const instant = parseInstant(timeSource);
  if (!instant || !dateOnlyStr) return instant;
  const [y, m, d] = String(dateOnlyStr).trim().split('-').map(Number);
  if (!y || !m || !d) return instant;
  return new Date(
    y,
    m - 1,
    d,
    instant.getHours(),
    instant.getMinutes(),
    instant.getSeconds(),
    instant.getMilliseconds()
  );
}

function combineDateAndTimeStrings(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const combined = new Date(`${String(dateStr).trim()}T${String(timeStr).trim()}`);
  return Number.isNaN(combined.getTime()) ? null : combined;
}

/** Same instant as Ventes list and thermal receipts. */
export function resolveSaleTransactionInstant(sale = {}) {
  return resolveSaleReceiptDate(sale);
}

/** Supplier / invoice payment instant. */
export function resolvePaymentTransactionInstant(payment = {}) {
  const instant =
    parseInstant(payment.paymentDate) ||
    parseInstant(payment.createdAt) ||
    parseInstant(payment.timestamp) ||
    parseInstant(payment.date);

  return instant || new Date();
}

/**
 * Expense payment instant: business date (picker) + wall-clock from createdAt/payment.
 */
export function resolveExpenseTransactionInstant(expense = {}) {
  const payment = expense.payment || {};
  const createdInstant =
    parseInstant(expense.createdAt) ||
    parseInstant(payment.createdAt) ||
    parseInstant(expense.timestamp) ||
    parseInstant(payment.timestamp);

  const businessDate =
    expense.date ||
    expense.paymentDate ||
    payment.paymentDate;

  if (businessDate && isDateOnlyValue(businessDate)) {
    const dateStr = String(businessDate).trim();

    if (expense.time) {
      const withTime = combineDateAndTimeStrings(dateStr, expense.time);
      if (withTime) return withTime;
    }

    if (createdInstant) {
      return combineDateWithInstantTime(dateStr, createdInstant);
    }

    const paymentInstant = parseInstant(payment.paymentDate);
    if (paymentInstant && !isDateOnlyValue(payment.paymentDate)) {
      return combineDateWithInstantTime(dateStr, paymentInstant);
    }

    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d, 0, 0, 0);
  }

  return (
    parseInstant(payment.paymentDate) ||
    createdInstant ||
    parseInstant(expense.paymentDate) ||
    parseInstant(businessDate) ||
    new Date()
  );
}

/**
 * @param {Object} entry
 * @param {'sale'|'expense'|'supplier_payment'|'payment'} [type]
 */
export function resolveTransactionInstant(entry = {}, type) {
  const resolvedType = type || entry.type;
  switch (resolvedType) {
    case 'sale':
      return resolveSaleTransactionInstant(entry);
    case 'expense':
      return resolveExpenseTransactionInstant(entry);
    case 'supplier_payment':
    case 'payment':
      return resolvePaymentTransactionInstant(entry);
    default:
      return parseInstant(entry.date) || new Date();
  }
}

export function toTransactionIso(instant) {
  const date = parseInstant(instant);
  return date ? date.toISOString() : null;
}

export function formatTransactionDate(instant, locale = 'fr-MR', fallback = '-') {
  return formatSafeDate(instant, locale, {}, fallback);
}

export function formatTransactionTime(instant, locale = 'fr-MR', fallback = '-') {
  return formatSafeTime(instant, locale, {}, fallback);
}

export function formatTransactionDateTime(instant, locale = 'fr-MR', fallback = '-') {
  return formatSafeDateTime(instant, locale, {}, fallback);
}
